/**
 * Mobile workspace-switcher dropdown. Extracted verbatim (behavior-preserving)
 * from the `{showWorkspaceSwitcher && (…)}` block in layout/Sidebar.tsx.
 *
 * The orchestrator still owns the `workspaceSwitcherOpen` open/close state and
 * the `showWorkspaceSwitcher` render gate; this component is purely
 * presentational and threads the session container data + actions through as
 * props. `getContainerDisplayName` is imported directly from the sibling utils.
 */

import { getContainerDisplayName } from '../utils';
import type { ContainerAccess } from '@/types/session';

interface WorkspaceSwitcherProps {
  containers: ContainerAccess[];
  selectedContainer: ContainerAccess | null;
  workspaceSwitcherOpen: boolean;
  setWorkspaceSwitcherOpen: (value: boolean | ((prev: boolean) => boolean)) => void;
  selectContainer: (containerId: string) => Promise<{ success: boolean; status?: number }>;
  setIsAddWorkspaceModalOpen: (value: boolean) => void;
}

export function WorkspaceSwitcher({
  containers,
  selectedContainer,
  workspaceSwitcherOpen,
  setWorkspaceSwitcherOpen,
  selectContainer,
  setIsAddWorkspaceModalOpen,
}: WorkspaceSwitcherProps) {
  return (
    <div className="sidebar-section sidebar-workspace-switcher-mobile">
      <div className="sidebar-workspace-dropdown-wrap">
        <button
          className="sidebar-workspace-dropdown-btn"
          onClick={() => setWorkspaceSwitcherOpen((o) => !o)}
        >
          <span className="sidebar-workspace-icon">
            {selectedContainer?.isOwner !== false ? (
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M3 9L12 2L21 9V20C21 20.5304 20.7893 21.0391 20.4142 21.4142C20.0391 21.7893 19.5304 22 19 22H5C4.46957 22 3.96086 21.7893 3.58579 21.4142C3.21071 21.0391 3 20.5304 3 20V9Z" />
                <path d="M9 22V12H15V22" />
              </svg>
            ) : (
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M17 21V19C17 17.9391 16.5786 16.9217 15.8284 16.1716C15.0783 15.4214 14.0609 15 13 15H5C3.93913 15 2.92172 15.4214 2.17157 16.1716C1.42143 16.9217 1 17.9391 1 19V21" />
                <circle cx="9" cy="7" r="4" />
              </svg>
            )}
          </span>
          <span className="sidebar-workspace-dropdown-name">
            {selectedContainer ? getContainerDisplayName(selectedContainer) : 'Workspace'}
          </span>
          <svg className={`sidebar-workspace-dropdown-chevron${workspaceSwitcherOpen ? ' open' : ''}`} width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <path d="M6 9l6 6 6-6" />
          </svg>
        </button>
        {workspaceSwitcherOpen && (
          <div className="sidebar-workspace-dropdown-list">
            {containers.map((container) => {
              const isSelected = container.id
                ? selectedContainer?.id === container.id
                : selectedContainer?.ownerId === container.ownerId;
              const displayName = getContainerDisplayName(container);
              return (
                <button
                  key={container.id || container.ownerId}
                  className={`sidebar-workspace-dropdown-option${isSelected ? ' active' : ''}`}
                  onClick={() => {
                    if (!isSelected) selectContainer(container.id || container.ownerId);
                    setWorkspaceSwitcherOpen(false);
                  }}
                >
                  <span className="sidebar-workspace-icon">
                    {container.isOwner ? (
                      <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M3 9L12 2L21 9V20C21 20.5304 20.7893 21.0391 20.4142 21.4142C20.0391 21.7893 19.5304 22 19 22H5C4.46957 22 3.96086 21.7893 3.58579 21.4142C3.21071 21.0391 3 20.5304 3 20V9Z" />
                        <path d="M9 22V12H15V22" />
                      </svg>
                    ) : (
                      <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M17 21V19C17 17.9391 16.5786 16.9217 15.8284 16.1716C15.0783 15.4214 14.0609 15 13 15H5C3.93913 15 2.92172 15.4214 2.17157 16.1716C1.42143 16.9217 1 17.9391 1 19V21" />
                        <circle cx="9" cy="7" r="4" />
                      </svg>
                    )}
                  </span>
                  <span className="sidebar-workspace-dropdown-name">
                    {displayName}
                    {container.isOwner && <span style={{ fontSize: 10, color: 'var(--color-text-faint, #aaa)', marginLeft: 4, fontWeight: 400 }}>owner</span>}
                  </span>
                  {isSelected && (
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M20 6L9 17L4 12" />
                    </svg>
                  )}
                  {container.permission === 'read' && (
                    <span className="sidebar-workspace-badge">View</span>
                  )}
                </button>
              );
            })}
            <div style={{ borderTop: '1px solid var(--color-border)', marginTop: 4, paddingTop: 4 }}>
              <button
                className="sidebar-workspace-dropdown-option"
                onClick={() => { setWorkspaceSwitcherOpen(false); setIsAddWorkspaceModalOpen(true); }}
                style={{ color: 'var(--color-brand)', fontWeight: 500 }}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
                  <line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>
                </svg>
                Add Workspace
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
